import { useState, useRef, useCallback } from 'react';
import { getAuth } from "firebase/auth";
import { estimateConversationTokens } from '../utils/tokenCounter';

/**
 * Custom hook for managing conversation state and API interactions
 * @param {number} MAX_CONTEXT_TOKENS - Maximum tokens allowed in context
 * @returns {Object} Conversation state and functions
 */
export const useConversation = (MAX_CONTEXT_TOKENS = 4000) => {
  const [query, setQuery] = useState('');
  const [responses, setResponses] = useState([]);
  const [loadingIndex, setLoadingIndex] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [detectedGoLinks, setDetectedGoLinks] = useState([]);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(null);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [totalContextTokens, setTotalContextTokens] = useState(0);
  const [arrowCycle, setArrowCycle] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const MAX_INPUT_LENGTH = 500;

  const latestMessageRef = useRef(null);

  // Update total context tokens when responses change
  const updateTotalContextTokens = useCallback(() => {
    const contextTokens = estimateConversationTokens(responses);
    setTotalContextTokens(contextTokens);
  }, [responses]);

  // Scroll to the latest message
  const scrollToLatestMessage = useCallback(() => {
    if (latestMessageRef.current) {
      latestMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, []);

  // Handle new conversation (reset state)
  const handleNewConversation = useCallback(() => {
    setResponses([]);
    setConversationId(null);
    setDetectedGoLinks([]);
    setFeedbackSubmitted(null);
    setQuery('');
  }, []);

  // Handle input change
  const handleInputChange = useCallback((e) => {
    const input = e.target.value;

    // Only update if within character limit
    if (input.length <= MAX_INPUT_LENGTH) {
      setQuery(input);
      setCharCount(input.length);
    }
  }, []);

  // Handle key press (Enter to send)
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, []);

  // Handle search with current query
  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;

    const newQuery = query;
    setQuery('');

    const queryTokens = MAX_INPUT_LENGTH / 4; // Rough estimate based on 4 chars per token

    // Check if adding this query might exceed context window
    if (totalContextTokens + queryTokens > MAX_CONTEXT_TOKENS * 0.75) {
      alert("This conversation is getting too long. Please start a new one.");
      return;
    }

    // Trigger animation
    setArrowCycle(prev => prev + 1);

    // Instantly add the query to the conversation array (no response yet)
    setResponses((prev) => [...prev, { query: newQuery, response: null }]);
    const newIndex = responses.length; // Index of the new query
    setLoadingIndex(newIndex);

    setTimeout(() => {
      scrollToLatestMessage();
    }, 200);

    try {
      const auth = getAuth();
      const idToken = await auth.currentUser.getIdToken();
      // Updated to use relative path which will be handled by the hosting rewrite rules
      const res = await fetch('/api/openai/smart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          prompt: newQuery,
          user_id: auth.currentUser.uid,
          conversation_id: conversationId
        })
      });

      const data = await res.json();

      if (data.error) {
        throw new Error(data.error);
      }

      // Extract the response from the enhanced backend structure
      const responseText = data.response || 'No response found.';

      // Store the conversation ID for future exchanges
      if (data.conversation_id) {
        setConversationId(data.conversation_id);
      }

      // Store any detected go links
      if (data.detected_go_links && data.detected_go_links.length > 0) {
        setDetectedGoLinks(data.detected_go_links);
      }

      // Update the specific item in `responses` with the actual text
      setResponses((prev) =>
        prev.map((item, index) =>
          index === newIndex
            ? { ...item, response: responseText }
            : item
        )
      );
    } catch (error) {
      // Show error as the "response"
      setResponses((prev) =>
        prev.map((item, index) =>
          index === newIndex
            ? { ...item, response: "Error: " + error.message }
            : item
        )
      );
    } finally {
      setLoadingIndex(null);
    }
  }, [query, responses.length, totalContextTokens, conversationId, scrollToLatestMessage]);

  // Handle search with provided text
  const handleSearchWithText = useCallback(async (searchText) => {
    if (!searchText.trim()) return;

    // Use the passed text directly instead of relying on the state
    const newQuery = searchText;
    setQuery('');

    const queryTokens = searchText.length / 4; // Rough estimate

    // Check if adding this query might exceed context window
    if (totalContextTokens + queryTokens > MAX_CONTEXT_TOKENS * 0.75) {
      alert("This conversation is getting too long. Please start a new one.");
      return;
    }

    // Trigger animation
    setArrowCycle(prev => prev + 1);

    // Instantly add the query to the conversation array (no response yet)
    setResponses((prev) => [...prev, { query: newQuery, response: null }]);
    const newIndex = responses.length; // Index of the new query
    setLoadingIndex(newIndex);

    setTimeout(() => {
      scrollToLatestMessage();
    }, 200);

    try {
      const auth = getAuth();
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch('/api/openai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          prompt: newQuery,
          user_id: auth.currentUser.uid,
          conversation_id: conversationId
        })
      });

      const data = await res.json();

      if (data.error) {
        throw new Error(data.error);
      }

      // Extract the response from the enhanced backend structure
      const responseText = data.response || 'No response found.';

      // Store the conversation ID for future exchanges
      if (data.conversation_id) {
        setConversationId(data.conversation_id);
      }

      // Store any detected go links
      if (data.detected_go_links && data.detected_go_links.length > 0) {
        setDetectedGoLinks(data.detected_go_links);
      }

      // Update the specific item in `responses` with the actual text
      setResponses((prev) =>
        prev.map((item, index) =>
          index === newIndex
            ? { ...item, response: responseText }
            : item
        )
      );
    } catch (error) {
      // Show error as the "response"
      setResponses((prev) =>
        prev.map((item, index) =>
          index === newIndex
            ? { ...item, response: "Error: " + error.message }
            : item
        )
      );
    } finally {
      setLoadingIndex(null);
    }
  }, [responses.length, totalContextTokens, conversationId, scrollToLatestMessage]);

  // Handle feedback submission
  const handleFeedback = useCallback(async (conversationId, rating) => {
    if (submittingFeedback) return;

    setSubmittingFeedback(true);

    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: conversationId,
          rating: rating, // 1 for helpful, 0 for not helpful
          comments: '' // Optional comment field
        })
      });

      if (res.ok) {
        setFeedbackSubmitted(rating);
      } else {
        console.error('Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setSubmittingFeedback(false);
    }
  }, [submittingFeedback]);

  return {
    query,
    setQuery,
    responses,
    loadingIndex,
    conversationId,
    detectedGoLinks,
    feedbackSubmitted,
    submittingFeedback,
    totalContextTokens,
    arrowCycle,
    charCount,
    MAX_INPUT_LENGTH,
    latestMessageRef,
    updateTotalContextTokens,
    scrollToLatestMessage,
    handleNewConversation,
    handleInputChange,
    handleKeyPress,
    handleSearch,
    handleSearchWithText,
    handleFeedback
  };
};